import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { isAuthenticated, getAuthHeaders } from '../utils/auth';
import CreditsModal from '../components/CreditsModal';
import StartTrialModal from '../components/StartTrialModal';
import { BACKEND_URL } from '../config/api';
import { clearCreditsCache, fetchCreditsCached } from '../services/credits';
import './InterviewSessions.css';

export default function InterviewSessions() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sessions, setSessions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [isTrial, setIsTrial] = useState(true);
  const [savedModalData, setSavedModalData] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [activeSessionTime, setActiveSessionTime] = useState(null);
  const hasFetchedSessions = useRef(false);
  
  // Trial cooldown state - cooldown starts after trial ENDS, not when it starts
  const TRIAL_COOLDOWN_MS = 15 * 60 * 1000; // 15 minutes
  const TRIAL_LAST_END_KEY = 'bk_last_trial_ended_at';
  const [trialCooldownRemainingMs, setTrialCooldownRemainingMs] = useState(0);

  // Refresh credits when component mounts (especially after session exit)
  useEffect(() => {
    // Clear cache and dispatch event to refresh credits in header
    clearCreditsCache();
    window.dispatchEvent(new CustomEvent('creditsUpdated'));
    
    // Listen for auth changes to refresh when user data is updated
    const handleAuthChange = () => {
      clearCreditsCache();
      window.dispatchEvent(new CustomEvent('creditsUpdated'));
    };
    
    window.addEventListener('authChanged', handleAuthChange);
    window.addEventListener('storage', handleAuthChange);
    
    return () => {
      window.removeEventListener('authChanged', handleAuthChange);
      window.removeEventListener('storage', handleAuthChange);
    };
  }, []);

  // Trial cooldown timer
  useEffect(() => {
    const checkCooldown = () => {
      const lastEndRaw = localStorage.getItem(TRIAL_LAST_END_KEY);
      const lastEnd = lastEndRaw ? Number(lastEndRaw) : 0;
      if (!Number.isNaN(lastEnd) && lastEnd > 0) {
        const elapsed = Date.now() - lastEnd;
        if (elapsed < TRIAL_COOLDOWN_MS) {
          setTrialCooldownRemainingMs(TRIAL_COOLDOWN_MS - elapsed);
        } else {
          setTrialCooldownRemainingMs(0);
        }
      } else {
        setTrialCooldownRemainingMs(0);
      }
    };

    checkCooldown();
    const intervalId = setInterval(checkCooldown, 1000);
    return () => clearInterval(intervalId);
  }, [TRIAL_COOLDOWN_MS]);

  // Check for active session
  useEffect(() => {
    const checkActiveSession = () => {
      const sessionEndTime = localStorage.getItem('session_end_time');
      const sessionActive = localStorage.getItem('session_active');
      
      if (sessionActive && sessionEndTime) {
        const remaining = Math.floor((parseInt(sessionEndTime) - Date.now()) / 1000);
        if (remaining > 0) {
          setActiveSessionTime(remaining);
        } else {
          setActiveSessionTime(null);
          localStorage.removeItem('session_active');
          localStorage.removeItem('session_end_time');
        }
      } else {
        setActiveSessionTime(null);
      }
    };

    checkActiveSession();
    const interval = setInterval(checkActiveSession, 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Format time as MM:SS
  // eslint-disable-next-line no-unused-vars
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins} mins ${secs} secs`;
  };

  // Format cooldown time as MM:SS
  const formatCooldownTime = (ms) => {
    const totalSeconds = Math.ceil(ms / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isTrialCooldownActive = trialCooldownRemainingMs > 0;

  // Fetch sessions from backend
  useEffect(() => {
    // Guard against double invocation in React StrictMode/dev
    if (hasFetchedSessions.current) return;
    hasFetchedSessions.current = true;

    // CRITICAL: Handle production hydration timing
    // Check auth immediately, but also recheck after brief delay for production builds
    if (!isAuthenticated()) {
      const recheckTimeout = setTimeout(() => {
        if (!isAuthenticated()) {
          navigate('/signup');
        } else {
          // Auth became valid after hydration, fetch sessions
          fetchSessionsData();
        }
      }, 100);
      return () => clearTimeout(recheckTimeout);
    }
    
    fetchSessionsData();
    
    async function fetchSessionsData() {
      setLoading(true);
      try {
        const email = localStorage.getItem('userEmail');
        
        // Cookie-based auth: credentials: 'include' sends JWT cookie automatically
        const headers = getAuthHeaders();
        
        const response = await fetch(`${BACKEND_URL}/api/sessions/list?email=${encodeURIComponent(email || '')}`, {
          method: 'GET',
          credentials: 'include',
          headers
        });
        if (response.ok) {
          const data = await response.json();
          // The API returns { sessions: [...], count: N }
          const sessionArr = Array.isArray(data.sessions) ? data.sessions : [];
          const mapped = sessionArr.map(session => ({
            company: session.company || '-',
            endsIn: session.duration || '-',
            aiUsage: session.credit !== undefined ? session.credit : '-',
            createdAt: session.createdAt ? new Date(session.createdAt).toLocaleString() : '-',
            createdAtRaw: session.createdAt,
            sessionId: session.sessionId || session._id || session.id,
            hasTranscript: session.hasTranscript || false
          }));
          // Sort by createdAt date descending (most recent first)
          mapped.sort((a, b) => {
            const dateA = a.createdAtRaw ? new Date(a.createdAtRaw).getTime() : 0;
            const dateB = b.createdAtRaw ? new Date(b.createdAtRaw).getTime() : 0;
            return dateB - dateA;
          });
          setSessions(mapped);
        } else {
          setSessions([]);
        }
      } catch (e) {
        setSessions([]);
      }
      setLoading(false);
    }
  }, [navigate]);

  // Check if we need to reopen trial modal after resume upload
  useEffect(() => {
    if (location.state?.reopenTrialModal && location.state?.trialModalData) {
      // console.log('Reopening trial modal with saved data:', location.state.trialModalData);
      setSavedModalData(location.state.trialModalData);
      setIsTrial(location.state.trialModalData.isTrial);
      setShowTrialModal(true);
      
      // Clear the state to prevent reopening on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  // Check if user has credits using centralized service
  const checkCredits = async () => {
    try {
      console.log('Checking credits from InterviewSessions...');
      const data = await fetchCreditsCached();
      const totalCredits = data.totalCredits || 0;
      console.log('User credits:', totalCredits);
      return totalCredits > 0;
    } catch (error) {
      console.error('Error checking credits:', error);
      return false;
    }
  };

  const handleStartTrial = async () => {
    // Open trial modal for trial session
    setIsTrial(true);
    setShowTrialModal(true);
  };

  const handleStartSession = async () => {
      // console.log('=== Start Session Clicked (InterviewSessions) ===');
    
    // Check if user has credits
    const hasCredits = await checkCredits();
    
      // console.log('Has credits result:', hasCredits);
    
    if (!hasCredits) {
      // console.log('Opening credits modal (no credits)');
      setShowCreditsModal(true);
    } else {
      // console.log('Opening trial modal with isTrial=false (has credits)');
      // Open trial modal for interview session (same flow as trial)
      setIsTrial(false); // Not a trial, it's a full interview
      setShowTrialModal(true);
    }
  };

  const totalSessions = sessions.length;
  const itemsPerPage = 10;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalSessions);
  const displayedSessions = sessions.slice(startIndex, endIndex);

  return (
    <div className="interview-sessions-page">
      <CreditsModal 
        isOpen={showCreditsModal} 
        onClose={() => setShowCreditsModal(false)} 
      />
      <StartTrialModal 
        isOpen={showTrialModal} 
        onClose={() => setShowTrialModal(false)}
        isTrial={isTrial}
      />
      
      <div className="sessions-header">
        <div className="title-section">
          <span className="icon-bubble">💬</span>
          <h1>Interview Sessions</h1>
        </div>
        <div className="action-buttons">
          <button 
            className={`btn-trial ${isTrialCooldownActive ? 'btn-trial-disabled' : ''}`} 
            onClick={handleStartTrial}
            disabled={isTrialCooldownActive}
          >
            {isTrialCooldownActive 
              ? `⏳ Trial available in ${formatCooldownTime(trialCooldownRemainingMs)}`
              : 'Start Trial Session'
            }
          </button>
          <button className="btn-start" onClick={handleStartSession}>
            Start Session
          </button>
        </div>
      </div>

      <div className="sessions-table-wrapper">
        <table className="sessions-table">
          <thead>
            <tr>
              <th>Company</th>
              <th>Duration</th>
              <th>AI Usage</th>
              <th>Created At</th>
              <th>Transcript</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="empty-state">
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', padding: '40px' }}>
                    <div className="spinner" style={{
                      border: '3px solid rgba(255, 255, 255, 0.1)',
                      borderTop: '3px solid #10b981',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    <span style={{ color: '#9ca3af' }}>Loading sessions...</span>
                  </div>
                </td>
              </tr>
            ) : displayedSessions.length === 0 ? (
              <tr>
                <td colSpan="5" className="empty-state">
                  No Interview Sessions yet.
                </td>
              </tr>
            ) : (
              displayedSessions.map((session, idx) => (
                <tr key={idx}>
                  <td>{session.company}</td>
                  <td>
                    {session.endsIn}
                  </td>
                  <td>{session.aiUsage}</td>
                  <td>{session.createdAt}</td>
                  <td>
                    <button
                      className="btn-view-transcript"
                      onClick={() => navigate(`/sessions/${session.sessionId}/transcript?company=${encodeURIComponent(session.company)}`)}
                      title="View transcript"
                    >
                      📝 View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="pagination">
          <div className="page-info">
            Page {currentPage} • Showing {startIndex + 1}-{endIndex} of {totalSessions}
          </div>
          <div className="page-controls">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              Previous
            </button>
            <button
              disabled={endIndex >= totalSessions}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {showTrialModal && (
        <StartTrialModal 
          isOpen={showTrialModal}
          onClose={() => setShowTrialModal(false)} 
          isTrial={isTrial}
          initialData={savedModalData}
        />
      )}
      {showCreditsModal && <CreditsModal onClose={() => setShowCreditsModal(false)} />}
    </div>
  );
}
